import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Plus, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "../scripts/AuthContext";
import { API_BASE_URL } from "@/lib/apiBase";
import { getFullName } from "@/lib/userUtils";
import OrgMemberAvatar from "@/components/avatars/OrgMemberAvatar";
import ChannelPostingPolicyFields from "@/components/dm/ChannelPostingPolicyFields";
import { createTeamChannel } from "@/lib/dmApi";
import { toastError } from "@/lib/toast";
import {
  entitlementMessageFor,
  getEntitlementCode,
  toastEntitlementOrError,
} from "@/lib/entitlementToast";

const STEPS = ["basics", "features", "channels", "invites"];
const STEP_LABELS = {
  basics: "Basics",
  features: "Features",
  channels: "Channels",
  invites: "Invite",
};

const emptyChannelDraft = () => ({
  key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  title: "",
  visibility: "private",
  searchable: true,
  posting_policy: "open",
  allowed_poster_ids: [],
  allowed_poster_users: [],
});

async function withRetry(fn, attempts = 2) {
  let lastErr;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      const status = err?.response?.status;
      if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
        throw err;
      }
    }
  }
  throw lastErr;
}

/**
 * Multi-step create-team wizard. Finish uses multiple API calls with compensating
 * DELETE if channels/invites fail after the team is created.
 */
export default function CreateTeamWizard({
  open,
  onOpenChange,
  orgId,
  currentUserId,
  onCreated,
  canManageSettings = false,
}) {
  const { accessToken, login } = useAuth();
  const [step, setStep] = useState(0);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState("organization");
  const [searchable, setSearchable] = useState(true);
  const [kanbanEnabled, setKanbanEnabled] = useState(true);

  const [extraChannels, setExtraChannels] = useState([]);
  const [editingChannel, setEditingChannel] = useState(null);

  const [memberQuery, setMemberQuery] = useState("");
  const [orgMembers, setOrgMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [selectedInviteIds, setSelectedInviteIds] = useState(() => new Set());

  const makeAuthenticatedRequest = async (requestFn, token = accessToken) => {
    try {
      return await requestFn(token);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        if (err.response.data?.message === "Token has expired") {
          const refreshResponse = await axios.post(
            `${API_BASE_URL}/auth/refresh`,
            null,
            { withCredentials: true }
          );
          const newAccessToken =
            refreshResponse.data.access_token ||
            refreshResponse.data.accessToken;
          login(newAccessToken);
          return await requestFn(newAccessToken);
        }
      }
      throw err;
    }
  };

  const reset = () => {
    setStep(0);
    setError(null);
    setSubmitting(false);
    setName("");
    setDescription("");
    setVisibility("organization");
    setSearchable(true);
    setKanbanEnabled(true);
    setExtraChannels([]);
    setEditingChannel(null);
    setMemberQuery("");
    setSelectedInviteIds(new Set());
  };

  useEffect(() => {
    if (!open) return;
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when opened
  }, [open]);

  useEffect(() => {
    if (!open || !orgId || !accessToken) return;
    let cancelled = false;
    (async () => {
      setMembersLoading(true);
      try {
        const res = await makeAuthenticatedRequest(async (token) =>
          axios.get(`${API_BASE_URL}/org/${orgId}/members`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        );
        if (!cancelled) setOrgMembers(res.data || []);
      } catch (err) {
        console.error("Failed to load org members for wizard:", err);
      } finally {
        if (!cancelled) setMembersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, orgId, accessToken]);

  const inviteCandidates = useMemo(() => {
    return (orgMembers || [])
      .map((m) => ({
        user: {
          ...(m.user || m),
          org_email: m.org_email ?? (m.user || m).org_email ?? null,
        },
        title: m.title,
        org_email: m.org_email ?? null,
      }))
      .filter(({ user: u }) => u?.user_id)
      .filter(({ user: u }) => u.user_id !== currentUserId);
  }, [orgMembers, currentUserId]);

  const filteredCandidates = useMemo(() => {
    const q = memberQuery.trim().toLowerCase();
    if (!q) return inviteCandidates;
    return inviteCandidates.filter(({ user: u, title, org_email }) => {
      const haystack = [
        u.first_name,
        u.last_name,
        getFullName(u),
        org_email || u.org_email,
        title,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [inviteCandidates, memberQuery]);

  const validateStep = () => {
    if (STEPS[step] === "basics") {
      if (!name.trim()) {
        setError("Team name is required");
        return false;
      }
    }
    if (STEPS[step] === "channels" && editingChannel) {
      if (!editingChannel.title.trim()) {
        setError("Channel title is required (or cancel the draft)");
        return false;
      }
    }
    setError(null);
    return true;
  };

  const goNext = () => {
    if (!validateStep()) return;
    if (editingChannel) {
      commitChannelDraft();
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const goBack = () => {
    setError(null);
    if (editingChannel) {
      setEditingChannel(null);
      return;
    }
    setStep((s) => Math.max(s - 1, 0));
  };

  const commitChannelDraft = () => {
    if (!editingChannel?.title.trim()) {
      setError("Channel title is required");
      return;
    }
    if (
      editingChannel.posting_policy === "custom" &&
      (!editingChannel.allowed_poster_ids ||
        editingChannel.allowed_poster_ids.length === 0)
    ) {
      setError("Select at least one person who can post");
      return;
    }
    setExtraChannels((list) => {
      const idx = list.findIndex((c) => c.key === editingChannel.key);
      if (idx >= 0) {
        const next = [...list];
        next[idx] = { ...editingChannel, title: editingChannel.title.trim() };
        return next;
      }
      return [
        ...list,
        { ...editingChannel, title: editingChannel.title.trim() },
      ];
    });
    setEditingChannel(null);
    setError(null);
  };

  const compensateDelete = async (token, teamId) => {
    try {
      await axios.delete(`${API_BASE_URL}/team/${teamId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { org_id: orgId },
      });
    } catch (delErr) {
      console.error("Compensating team delete failed:", delErr);
      throw new Error(
        `Setup failed and cleanup could not remove team “${name.trim()}” (id ${teamId}). Please delete it manually or contact support.`
      );
    }
  };

  const handleFinish = async () => {
    if (!validateStep() || submitting) return;
    if (editingChannel) {
      commitChannelDraft();
      return;
    }

    setSubmitting(true);
    setError(null);
    let createdTeamId = null;

    try {
      await makeAuthenticatedRequest(async (token) => {
        const createRes = await axios.post(
          `${API_BASE_URL}/team/create`,
          {
            org_id: orgId,
            name: name.trim(),
            description: description.trim() || undefined,
            visibility,
            searchable:
              visibility === "organization" ? true : searchable !== false,
            kanban_enabled: kanbanEnabled,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const team = createRes.data?.data || createRes.data;
        createdTeamId = team.team_id ?? team.teamId;
        if (!createdTeamId) {
          throw new Error("Team created but no team id returned");
        }

        for (const ch of extraChannels) {
          const teamIdForChannel = createdTeamId;
          await withRetry(() =>
            createTeamChannel(token, orgId, teamIdForChannel, {
              title: ch.title,
              visibility: ch.visibility,
              searchable:
                ch.visibility === "private" ? ch.searchable !== false : true,
              posting_policy: ch.posting_policy || "open",
              ...(ch.posting_policy === "custom"
                ? { allowed_poster_ids: ch.allowed_poster_ids || [] }
                : {}),
            })
          );
        }

        for (const userId of selectedInviteIds) {
          const teamIdForInvite = createdTeamId;
          try {
            await withRetry(() =>
              axios.post(
                `${API_BASE_URL}/invitations`,
                {
                  org_id: orgId,
                  team_id: teamIdForInvite,
                  user_id: userId,
                  role: "member",
                },
                { headers: { Authorization: `Bearer ${token}` } }
              )
            );
          } catch (invErr) {
            const msg = String(
              invErr.response?.data?.message || invErr.message || ""
            ).toLowerCase();
            if (
              msg.includes("already") ||
              msg.includes("pending") ||
              msg.includes("exist")
            ) {
              continue;
            }
            throw invErr;
          }
        }

        return team;
      });

      onOpenChange(false);
      if (onCreated) await onCreated(createdTeamId);
    } catch (err) {
      console.error("Create team wizard failed:", err);
      const entitlementCopy = getEntitlementCode(err)
        ? entitlementMessageFor(err, { canManageSettings })
        : null;
      if (entitlementCopy) {
        toastEntitlementOrError(err, "Failed to create team", {
          canManageSettings,
          orgId,
        });
      }
      if (createdTeamId) {
        try {
          await makeAuthenticatedRequest(async (token) =>
            compensateDelete(token, createdTeamId)
          );
          setError(
            entitlementCopy ||
              err.response?.data?.message ||
              err.message ||
              "Team setup failed. Changes were rolled back — try again."
          );
        } catch (cleanupErr) {
          setError(cleanupErr.message || String(cleanupErr));
          toastError(cleanupErr, "Team cleanup failed");
        }
      } else {
        setError(
          entitlementCopy ||
            err.response?.data?.message ||
            err.message ||
            "Failed to create team"
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (next) => {
    if (submitting) return;
    if (!next) reset();
    onOpenChange(next);
  };

  const toggleInvite = (userId) => {
    setSelectedInviteIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const stepKey = STEPS[step];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Team</DialogTitle>
          <DialogDescription>
            Step {step + 1} of {STEPS.length}: {STEP_LABELS[stepKey]}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 flex-wrap pb-2">
          {STEPS.map((s, i) => (
            <Badge
              key={s}
              variant={i === step ? "default" : i < step ? "secondary" : "outline"}
            >
              {i + 1}. {STEP_LABELS[s]}
            </Badge>
          ))}
        </div>

        <div className="space-y-4 py-2">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {stepKey === "basics" && (
            <>
              <div>
                <Label htmlFor="wizard-team-name">Name *</Label>
                <Input
                  id="wizard-team-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter team name"
                  disabled={submitting}
                />
              </div>
              <div>
                <Label htmlFor="wizard-team-desc">Description / purpose</Label>
                <textarea
                  id="wizard-team-desc"
                  className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What is this team for?"
                  disabled={submitting}
                />
              </div>
              <div>
                <Label htmlFor="wizard-team-visibility">Visibility</Label>
                <select
                  id="wizard-team-visibility"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={visibility}
                  disabled={submitting}
                  onChange={(e) => {
                    const v = e.target.value;
                    setVisibility(v);
                    if (v === "organization") setSearchable(true);
                  }}
                >
                  <option value="organization">Organization-wide</option>
                  <option value="restricted">Restricted</option>
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="wizard-team-searchable"
                  checked={visibility === "organization" ? true : searchable}
                  disabled={visibility === "organization" || submitting}
                  onChange={(e) => setSearchable(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label
                  htmlFor="wizard-team-searchable"
                  className={`text-sm font-normal ${
                    visibility === "organization" ? "text-muted-foreground" : ""
                  }`}
                >
                  {visibility === "organization"
                    ? "Searchable (always on for organization-wide teams)"
                    : "Searchable (team can be found in search)"}
                </Label>
              </div>
            </>
          )}

          {stepKey === "features" && (
            <div className="space-y-3">
              <div className="flex items-start gap-3 rounded-md border p-4">
                <input
                  type="checkbox"
                  id="wizard-kanban"
                  className="mt-1 h-4 w-4 rounded border-gray-300"
                  checked={kanbanEnabled}
                  disabled={submitting}
                  onChange={(e) => setKanbanEnabled(e.target.checked)}
                />
                <div>
                  <Label htmlFor="wizard-kanban">Kanban board</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Track work with lanes and cards. You can turn this on or off
                    later in team settings. When off, existing cards stay stored
                    but are hidden from feeds and the board.
                  </p>
                </div>
              </div>
            </div>
          )}

          {stepKey === "channels" && (
            <div className="space-y-4">
              <div className="rounded-md border bg-muted/30 p-3">
                <p className="text-sm font-medium">Default channel</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Always created as{" "}
                  <span className="font-medium text-foreground">
                    {name.trim() || "Team name"}
                  </span>
                  . Visibility follows the team (
                  {visibility === "organization" ? "public" : "private"}).
                </p>
              </div>

              {extraChannels.length > 0 && !editingChannel && (
                <ul className="space-y-2">
                  {extraChannels.map((ch) => (
                    <li
                      key={ch.key}
                      className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm"
                    >
                      <span>
                        <span className="font-medium">{ch.title}</span>
                        <span className="text-muted-foreground ml-2">
                          {ch.visibility} · {ch.posting_policy || "open"}
                        </span>
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={submitting}
                        onClick={() =>
                          setExtraChannels((list) =>
                            list.filter((c) => c.key !== ch.key)
                          )
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}

              {editingChannel ? (
                <div className="space-y-3 rounded-md border p-3">
                  <div>
                    <Label>Channel title</Label>
                    <Input
                      value={editingChannel.title}
                      onChange={(e) =>
                        setEditingChannel({
                          ...editingChannel,
                          title: e.target.value,
                        })
                      }
                      placeholder="e.g. announcements"
                      disabled={submitting}
                    />
                  </div>
                  <div>
                    <Label>Visibility</Label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={editingChannel.visibility}
                      disabled={submitting}
                      onChange={(e) => {
                        const v = e.target.value;
                        setEditingChannel({
                          ...editingChannel,
                          visibility: v,
                          posting_policy:
                            v === "public" && visibility === "restricted"
                              ? "team_only"
                              : editingChannel.posting_policy,
                        });
                      }}
                    >
                      <option value="private">Private</option>
                      <option value="public">Organization-wide</option>
                    </select>
                  </div>
                  {editingChannel.visibility === "private" && (
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="wizard-ch-searchable"
                        checked={editingChannel.searchable !== false}
                        disabled={submitting}
                        onChange={(e) =>
                          setEditingChannel({
                            ...editingChannel,
                            searchable: e.target.checked,
                          })
                        }
                      />
                      <Label htmlFor="wizard-ch-searchable" className="font-normal">
                        Searchable
                      </Label>
                    </div>
                  )}
                  <ChannelPostingPolicyFields
                    accessToken={accessToken}
                    orgId={orgId}
                    postingPolicy={editingChannel.posting_policy || "open"}
                    onPostingPolicyChange={(p) =>
                      setEditingChannel({
                        ...editingChannel,
                        posting_policy: p,
                      })
                    }
                    selectedIds={editingChannel.allowed_poster_ids || []}
                    onSelectedIdsChange={(ids) =>
                      setEditingChannel({
                        ...editingChannel,
                        allowed_poster_ids: ids,
                      })
                    }
                    selectedUsers={editingChannel.allowed_poster_users || []}
                    enabled={!submitting}
                  />
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setEditingChannel(null)}
                      disabled={submitting}
                    >
                      Cancel
                    </Button>
                    <Button type="button" onClick={commitChannelDraft} disabled={submitting}>
                      Add channel
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  disabled={submitting}
                  onClick={() => setEditingChannel(emptyChannelDraft())}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add another channel
                </Button>
              )}
            </div>
          )}

          {stepKey === "invites" && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Optionally invite organization members. You can skip and invite
                later.
              </p>
              <Input
                type="search"
                value={memberQuery}
                onChange={(e) => setMemberQuery(e.target.value)}
                placeholder="Search by name, org email, or title…"
                disabled={submitting}
              />
              <div className="max-h-64 overflow-y-auto border rounded-md divide-y">
                {membersLoading ? (
                  <p className="p-3 text-sm text-muted-foreground">
                    Loading members…
                  </p>
                ) : filteredCandidates.length === 0 ? (
                  <p className="p-3 text-sm text-muted-foreground">
                    {inviteCandidates.length === 0
                      ? "No other organization members to invite."
                      : `No members match “${memberQuery.trim()}”.`}
                  </p>
                ) : (
                  filteredCandidates.map(({ user: u, title, org_email }) => {
                    const selected = selectedInviteIds.has(u.user_id);
                    const contactEmail = org_email || u.org_email;
                    return (
                      <button
                        key={u.user_id}
                        type="button"
                        disabled={submitting}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/60 disabled:opacity-50 flex items-center gap-3 ${
                          selected ? "bg-primary/5" : ""
                        }`}
                        onClick={() => toggleInvite(u.user_id)}
                      >
                        <input
                          type="checkbox"
                          readOnly
                          checked={selected}
                          className="h-4 w-4"
                        />
                        <OrgMemberAvatar
                          user={u}
                          className="h-8 w-8 shrink-0"
                          fallbackClassName="text-[10px]"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="font-medium block truncate">
                            {getFullName(u) || "Unknown"}
                          </span>
                          {[title, contactEmail].filter(Boolean).length > 0 ? (
                            <span className="text-xs text-muted-foreground block truncate">
                              {[title, contactEmail].filter(Boolean).join(" · ")}
                            </span>
                          ) : null}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
              {selectedInviteIds.size > 0 && (
                <p className="text-xs text-muted-foreground">
                  {selectedInviteIds.size} selected
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          {step > 0 || editingChannel ? (
            <Button variant="outline" onClick={goBack} disabled={submitting}>
              Back
            </Button>
          ) : null}
          {step < STEPS.length - 1 || editingChannel ? (
            <Button onClick={goNext} disabled={submitting}>
              Next
            </Button>
          ) : (
            <Button onClick={handleFinish} disabled={submitting}>
              {submitting ? "Creating…" : "Create Team"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
